import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserCheck, Plus, LogIn, LogOut, Users, Clock, Trash2, Search } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function VisitorsPage() {
  const [visitors, setVisitors] = useState({ data: [], total: 0, today_count: 0, active_count: 0 });
  const [loading, setLoading] = useState(true);
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [form, setForm] = useState({ name: '', company: '', phone: '', purpose: '', host_employee: '', id_type: '', id_number: '' });
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  const fetchData = async () => {
    try {
      const res = await api.get('/visitors', { params: { page, limit: 30, status: filter || undefined, date: dateFilter || undefined } });
      setVisitors(res.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [page, filter, dateFilter]);

  const handleCheckIn = async () => {
    if (!form.name.trim()) { toast.error('Visitor name required'); return; }
    try { await api.post('/visitors/check-in', form); toast.success('Visitor checked in'); setShowCheckIn(false); setForm({ name: '', company: '', phone: '', purpose: '', host_employee: '', id_type: '', id_number: '' }); fetchData(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleCheckOut = async (id) => {
    try { await api.put(`/visitors/${id}/check-out`); toast.success('Visitor checked out'); fetchData(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="visitors-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-lg shadow-sky-500/20">
              <UserCheck size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight dark:text-white">Visitor Management</h1>
              <p className="text-sm text-muted-foreground">Track office visitors</p>
            </div>
          </div>
          <Button onClick={() => setShowCheckIn(true)} data-testid="check-in-visitor-btn"><LogIn size={14} className="mr-1.5" />Check In Visitor</Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          <Card data-testid="stat-today"><CardContent className="pt-3 pb-2"><div className="text-xs text-muted-foreground">Today</div><div className="text-2xl font-bold dark:text-white">{visitors.today_count}</div></CardContent></Card>
          <Card className="border-l-4 border-l-emerald-500" data-testid="stat-active"><CardContent className="pt-3 pb-2"><div className="text-xs text-muted-foreground">Currently In</div><div className="text-2xl font-bold text-success">{visitors.active_count}</div></CardContent></Card>
          <Card data-testid="stat-total"><CardContent className="pt-3 pb-2"><div className="text-xs text-muted-foreground">Total</div><div className="text-2xl font-bold dark:text-white">{visitors.total}</div></CardContent></Card>
        </div>

        <div className="flex gap-3">
          <Select value={filter || 'all'} onValueChange={(v) => { setFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-36" data-testid="visitor-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="checked_in">Checked In</SelectItem><SelectItem value="checked_out">Checked Out</SelectItem></SelectContent>
          </Select>
          <Input type="date" value={dateFilter} onChange={(e) => { setDateFilter(e.target.value); setPage(1); }} className="w-40" data-testid="visitor-date-filter" />
        </div>

        <Card>
          <CardContent className="pt-4">
            {loading ? <div className="text-center py-8 text-muted-foreground">Loading...</div> : (visitors.data || []).length === 0 ? (
              <div className="text-center py-12"><UserCheck size={32} className="mx-auto text-muted-foreground mb-2" /><p className="text-sm text-muted-foreground">No visitors found.</p></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="visitors-table">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="pb-2 pr-3">Name</th><th className="pb-2 pr-3">Company</th><th className="pb-2 pr-3">Purpose</th><th className="pb-2 pr-3">Host</th><th className="pb-2 pr-3">In</th><th className="pb-2 pr-3">Out</th><th className="pb-2">Status</th><th className="pb-2"></th>
                  </tr></thead>
                  <tbody>
                    {visitors.data.map(v => (
                      <tr key={v.id} className="border-b last:border-0 hover:bg-muted/50 dark:hover:bg-muted/50" data-testid={`visitor-row-${v.id}`}>
                        <td className="py-2 pr-3 font-medium dark:text-white">{v.name}</td>
                        <td className="py-2 pr-3 text-xs">{v.company || '-'}</td>
                        <td className="py-2 pr-3 text-xs">{v.purpose || '-'}</td>
                        <td className="py-2 pr-3 text-xs">{v.host_employee || '-'}</td>
                        <td className="py-2 pr-3 text-xs">{v.check_in_time ? new Date(v.check_in_time).toLocaleTimeString() : '-'}</td>
                        <td className="py-2 pr-3 text-xs">{v.check_out_time ? new Date(v.check_out_time).toLocaleTimeString() : '-'}</td>
                        <td className="py-2 pr-3"><Badge variant={v.status === 'checked_in' ? 'default' : 'secondary'} className="text-[10px]">{v.status === 'checked_in' ? 'In' : 'Out'}</Badge></td>
                        <td className="py-2">{v.status === 'checked_in' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleCheckOut(v.id)} data-testid={`checkout-${v.id}`}><LogOut size={12} className="mr-1" />Out</Button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={showCheckIn} onOpenChange={setShowCheckIn}>
          <DialogContent data-testid="check-in-dialog">
            <DialogHeader><DialogTitle>Check In Visitor</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} data-testid="visitor-name-input" className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Company</Label><Input value={form.company} onChange={(e) => setForm(f => ({ ...f, company: e.target.value }))} className="mt-1" /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} className="mt-1" /></div>
              </div>
              <div><Label>Purpose</Label><Input value={form.purpose} onChange={(e) => setForm(f => ({ ...f, purpose: e.target.value }))} placeholder="Meeting, Delivery, Interview..." className="mt-1" /></div>
              <div><Label>Host Employee</Label><Input value={form.host_employee} onChange={(e) => setForm(f => ({ ...f, host_employee: e.target.value }))} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>ID Type</Label><Input value={form.id_type} onChange={(e) => setForm(f => ({ ...f, id_type: e.target.value }))} placeholder="Iqama, Passport" className="mt-1" /></div>
                <div><Label>ID Number</Label><Input value={form.id_number} onChange={(e) => setForm(f => ({ ...f, id_number: e.target.value }))} className="mt-1" /></div>
              </div>
              <Button onClick={handleCheckIn} className="w-full" data-testid="submit-check-in-btn"><LogIn size={14} className="mr-1.5" />Check In</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
